import React, { useState } from 'react';
import { CheckCircle2, ChefHat, Leaf, Activity, Shield, Check, Gift, Heart, ArrowRight, Sparkles, Calendar, ShieldCheck, Package, AlertTriangle, XCircle, ShieldAlert, FileText, ShoppingCart, Headphones, Tag, Star, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { useOrderBumps, getActiveCheckoutUrl, getOrderTotal, getItemCount } from '../../hooks/useOrderBumps';
import { UpsellModal } from '../UpsellModal';
interface SolutionSectionWithCheckoutProps {
  checkoutUrl: string;
}
export function SolutionSectionWithCheckout({
  checkoutUrl
}: SolutionSectionWithCheckoutProps) {
  const [showBonos, setShowBonos] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const {
    bump1Selected,
    bump2Selected,
    setBump1Selected,
    setBump2Selected
  } = useOrderBumps();
  const activeCheckoutUrl = getActiveCheckoutUrl(checkoutUrl, bump1Selected, bump2Selected);
  const orderTotal = getOrderTotal(bump1Selected, bump2Selected);
  const itemCount = getItemCount(bump1Selected, bump2Selected);
  const DISCOUNT_CHECKOUT_URL = 'https://pay.hotmart.com/Q104389187Y?off=h96re49q&checkoutMode=10&bid=1771440501662';
  const handleCheckoutClick = (e: React.MouseEvent) => {
    if (!bump1Selected && !bump2Selected) {
      e.preventDefault();
      setShowUpsell(true);
      return;
    }
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: '30 Recetas Anti-Inflamatorias para Mascotas',
        content_category: 'E-book',
        value: orderTotal,
        currency: 'COP',
        num_items: itemCount
      });
    }
  };
  return <section id="solution-section" className="py-16 bg-stone-50/30 overflow-hidden" data-id="element-946">
      <UpsellModal isOpen={showUpsell} onClose={() => setShowUpsell(false)} baseCheckoutUrl={checkoutUrl} discountCheckoutUrl={DISCOUNT_CHECKOUT_URL} data-id="element-947" />

      {/* ── SOLUTION CONTENT ── */}
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-948">
        <div className="flex flex-col md:flex-row gap-10 items-center" data-id="element-949">
          <div className="w-full md:w-1/2" data-id="element-950">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-951">
              🥗 PROGRAMA CLÍNICO DE <br data-id="element-952" />
              <span className="text-amber-600" data-id="element-953">
                NUTRICIÓN NATURAL PARA PERROS
              </span>
            </h2>

            <p className="text-lg text-gray-600 mb-8 border-l-4 border-teal-400 pl-4 font-medium" data-id="element-954">
              Creado para transformar la salud de tu mascota desde adentro hacia
              afuera.
            </p>

            <div className="space-y-4 mb-10" data-id="element-955">
              <p className="font-bold text-gray-800 mb-4 text-lg" data-id="element-956">
                Creado para:
              </p>
              <div className="flex items-center gap-3" data-id="element-957">
                <CheckCircle2 className="w-6 h-6 text-teal-500 fill-teal-100 flex-shrink-0" data-id="element-958" />
                <span className="text-lg font-medium text-gray-800" data-id="element-959">
                  Reducir la inflamación interna
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-960">
                <CheckCircle2 className="w-6 h-6 text-teal-500 fill-teal-100 flex-shrink-0" data-id="element-961" />
                <span className="text-lg font-medium text-gray-800" data-id="element-962">
                  Restaurar los intestinos
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-963">
                <CheckCircle2 className="w-6 h-6 text-teal-500 fill-teal-100 flex-shrink-0" data-id="element-964" />
                <span className="text-lg font-medium text-gray-800" data-id="element-965">
                  Fortalecer el sistema inmunológico
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-966">
                <CheckCircle2 className="w-6 h-6 text-teal-500 fill-teal-100 flex-shrink-0" data-id="element-967" />
                <span className="text-lg font-medium text-gray-800" data-id="element-968">
                  Desintoxicar el cuerpo
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-969">
                <CheckCircle2 className="w-6 h-6 text-teal-500 fill-teal-100 flex-shrink-0" data-id="element-970" />
                <span className="text-lg font-medium text-gray-800" data-id="element-971">
                  Restaurar energía, vitalidad y bienestar
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-972">
                <CheckCircle2 className="w-6 h-6 text-teal-500 fill-teal-100 flex-shrink-0" data-id="element-973" />
                <span className="text-lg font-medium text-gray-800" data-id="element-974">
                  Proteger y alargar la vida de tu mascota
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden shadow-lg border-2 border-amber-100" data-id="element-975">
              <img src="https://cdn.magicpatterns.com/uploads/sKqNdoUGXRj7v9q5JngUP4/heylloyd-image-1769032443021.png" alt="Preparing healthy homemade dog food" className="w-full h-auto" data-id="element-976" />
            </div>
          </div>

          <div className="w-full md:w-1/2" data-id="element-977">
            <div className="relative group" data-id="element-978">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-100 to-stone-100 rounded-[2rem] transform rotate-3 scale-105 opacity-50 group-hover:rotate-6 transition-transform duration-500" data-id="element-979"></div>

              <div className="relative bg-white rounded-[2rem] p-8 md:p-12 border-2 border-teal-200 shadow-xl" data-id="element-980">
                <div className="absolute -top-6 -right-6 bg-teal-600 text-white p-4 rounded-full shadow-lg transform rotate-12" data-id="element-981">
                  <ChefHat className="w-8 h-8" data-id="element-982" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-983">
                  ¿Por qué funciona tan rápido?
                </h3>
                <p className="text-lg text-gray-600 text-center mb-8 leading-relaxed" data-id="element-984">
                  Porque utiliza ingredientes que el cuerpo de tu mascota
                  entiende, acepta y utiliza para regenerarse.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-985">
                  <div className="bg-teal-50 p-4 rounded-xl shadow-sm text-center border border-teal-100" data-id="element-986">
                    <span className="block text-2xl mb-2" data-id="element-987">🌿</span>
                    <span className="text-sm font-bold text-gray-800 block" data-id="element-988">
                      Naturales
                    </span>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-xl shadow-sm text-center border border-amber-100" data-id="element-989">
                    <span className="block text-2xl mb-2" data-id="element-990">⚡</span>
                    <span className="text-sm font-bold text-gray-800 block" data-id="element-991">
                      Funcionales
                    </span>
                  </div>
                  <div className="bg-rose-50 p-4 rounded-xl shadow-sm text-center border border-rose-100" data-id="element-992">
                    <span className="block text-2xl mb-2" data-id="element-993">🔥</span>
                    <span className="text-sm font-bold text-gray-800 block" data-id="element-994">
                      Antiinflamatorios
                    </span>
                  </div>
                  <div className="bg-stone-50 p-4 rounded-xl shadow-sm text-center border border-stone-200" data-id="element-995">
                    <span className="block text-2xl mb-2" data-id="element-996">👨‍🍳</span>
                    <span className="text-sm font-bold text-gray-800 block" data-id="element-997">
                      Fáciles de preparar
                    </span>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl shadow-sm text-center border border-green-100" data-id="element-998">
                    <span className="block text-2xl mb-2" data-id="element-999">💰</span>
                    <span className="text-sm font-bold text-gray-800 block" data-id="element-1000">
                      Económicos
                    </span>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl shadow-sm text-center border border-blue-100" data-id="element-1001">
                    <span className="block text-2xl mb-2" data-id="element-1002">✨</span>
                    <span className="text-sm font-bold text-gray-800 block" data-id="element-1003">
                      Alta absorción
                    </span>
                  </div>
                </div>

                <div className="bg-teal-100 p-4 rounded-xl border border-teal-200 text-center" data-id="element-1004">
                  <p className="text-teal-800 font-bold" data-id="element-1005">
                    Es el alimento que el cuerpo de tu mascota entiende, acepta
                    y utiliza para regenerarse.
                  </p>
                </div>

                {/* Authority Section */}
                <div className="relative bg-gradient-to-br from-amber-50 to-stone-100 rounded-[2rem] p-8 md:p-10 border-2 border-amber-200 shadow-lg mt-8" data-id="element-1006">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1007">
                    ¿Quién desarrolló este protocolo?
                  </h3>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-6 text-center max-w-2xl mx-auto" data-id="element-1008">
                    El Programa Clínico de Nutrición Natural para Perros está
                    inspirado en la metodología del{' '}
                    <strong className="text-amber-700" data-id="element-1009">
                      Dr. Rennan Viegas
                    </strong>
                    , médico veterinario con más de 18 años de experiencia y una
                    comunidad de más de 620.000 seguidores.
                  </p>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-8 text-center max-w-2xl mx-auto" data-id="element-1010">
                    Su enfoque en nutrición natural y prevención ha transformado
                    la forma en que miles de dueños alimentan y cuidan la salud
                    de sus mascotas.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1011">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-amber-100 flex flex-col items-center text-center" data-id="element-1012">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-3" data-id="element-1013">
                        <Shield className="w-6 h-6 text-amber-700" data-id="element-1014" />
                      </div>
                      <p className="text-2xl font-bold text-amber-700 mb-1" data-id="element-1015">
                        +18 años
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-1016">
                        experiencia
                      </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-amber-100 flex flex-col items-center text-center" data-id="element-1017">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3" data-id="element-1018">
                        <Leaf className="w-6 h-6 text-teal-600" data-id="element-1019" />
                      </div>
                      <p className="text-sm text-gray-800 font-bold mb-1" data-id="element-1020">
                        Enfoque en
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-1021">
                        nutrición natural
                      </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-amber-100 flex flex-col items-center text-center" data-id="element-1022">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3" data-id="element-1023">
                        <Activity className="w-6 h-6 text-green-600" data-id="element-1024" />
                      </div>
                      <p className="text-sm text-gray-800 font-bold mb-1" data-id="element-1025">
                        Método aplicado
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-1026">
                        por miles de dueños
                      </p>
                    </div>
                  </div>
                </div>

                {/* Video Section */}
                <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-[2rem] p-8 md:p-10 border-2 border-blue-200 shadow-lg mt-8" data-id="element-1027">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1028">
                    Ella lo comprobó con su perro
                  </h3>
                  <div className="rounded-xl overflow-hidden shadow-xl border-2 border-blue-100" data-id="element-1029">
                    <video controls preload="auto" playsInline className="w-full h-auto" style={{
                    maxHeight: '600px'
                  }} data-id="element-1030">
                      <source src="https://ydgkmdmvhrniztrhtrxq.supabase.co/storage/v1/object/sign/audio%20receta/freepik_1080x1920_30fps_14228.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80NDNkYTY1ZS1iYWM1LTQyYWQtODMzNS1iNWE4N2M0NDYxNmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyByZWNldGEvZnJlZXBpa18xMDgweDE5MjBfMzBmcHNfMTQyMjgubXA0IiwiaWF0IjoxNzcxNzY2MDY4LCJleHAiOjE4MDMzMDIwNjh9.jJthctnqJad8IknGrrF_q9s4zxF0FqSFHDuaUtaZTD8#t=0.1" type="video/mp4" data-id="element-1031" />
                      Tu navegador no soporta el elemento de video.
                    </video>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CHECKOUT OFFER ── */}
      <div className="container mx-auto px-4 max-w-6xl mt-20" data-id="element-1032">
        <div className="text-center mb-16" data-id="element-1033">
          <div className="inline-flex items-center gap-2 bg-rose-700 text-white px-6 py-2 rounded-full text-lg font-bold uppercase tracking-wide mb-6 animate-pulse" data-id="element-1034">
            <Sparkles className="w-5 h-5" data-id="element-1035" />
            OFERTA LIMITADA – SOLO POR HOY
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1036">
            PROGRAMA CLÍNICO DE NUTRICIÓN NATURAL PARA PERROS
          </h2>
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-1037">
          <div id="complete-package" className="bg-white p-8 md:p-10 rounded-2xl shadow-xl shadow-amber-100 border-2 border-amber-100 relative overflow-hidden group hover:border-amber-300 transition-colors duration-300" data-id="element-1038">
            {/* Bundle Badge */}
            <div className="flex items-center justify-center mb-6" data-id="element-1039">
              <div className="bg-gradient-to-r from-amber-600 to-rose-700 text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wide flex items-center gap-2 shadow-lg transform group-hover:scale-105 transition-transform" data-id="element-1040">
                <Sparkles className="w-4 h-4 text-yellow-200" data-id="element-1041" />
                TODO LO QUE TU PERRO NECESITA PARA DESINFLAMARSE
              </div>
            </div>

            {/* Complete Package Image */}
            <div className="mb-8 flex justify-center" data-id="element-1042">
              <div className="rounded-xl overflow-hidden shadow-lg border-2 border-amber-50" data-id="element-1043">
                <img src="https://cdn.magicpatterns.com/uploads/gnEaoFwYpACgNWsD1np27d/ChatGPT_Image_10_mar_2026,_04_47_53_p.m..png" alt="Programa Clínico de Nutrición Natural - 30 Recetas Anti-Inflamación para Perros" className="w-full h-auto object-cover" data-id="element-1044" />
              </div>
            </div>

            <div className="flex items-center gap-4 mb-6" data-id="element-1045">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-700" data-id="element-1046">
                <Heart className="w-6 h-6 fill-current" data-id="element-1047" />
              </div>
              <div data-id="element-1048">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1049">
                  Paquete Completo
                </h3>
                <p className="text-sm text-amber-700 font-bold uppercase tracking-wider" data-id="element-1050">
                  E-book Principal + 3 Regalos Gratis{' '}
                  <span className="text-rose-700" data-id="element-1051">(Solo por hoy)</span>
                </p>
              </div>
            </div>

            {/* Checklist */}
            <div className="bg-gradient-to-br from-amber-50 to-stone-100 p-4 rounded-xl border border-amber-200 mb-4" data-id="element-1052">
              <div className="flex items-center gap-2 mb-2" data-id="element-1053">
                <Package className="w-4 h-4 text-amber-700" data-id="element-1054" />
                <p className="text-sm font-bold text-gray-800" data-id="element-1055">Incluye:</p>
              </div>
              <div className="space-y-1.5" data-id="element-1056">
                <div className="flex items-start gap-2" data-id="element-1057">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1058" />
                  <span className="text-xs text-gray-700" data-id="element-1059">
                    Introducción completa
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1060">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1061" />
                  <span className="text-xs text-gray-700" data-id="element-1062">
                    Guía para calcular cantidades
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1063">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1064" />
                  <span className="text-xs text-gray-700" data-id="element-1065">
                    Recetas con <strong data-id="element-1066">pollo</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1067">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1068" />
                  <span className="text-xs text-gray-700" data-id="element-1069">
                    Recetas con <strong data-id="element-1070">carne de res</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1071">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1072" />
                  <span className="text-xs text-gray-700" data-id="element-1073">
                    Recetas con <strong data-id="element-1074">cerdo</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1075">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1076" />
                  <span className="text-xs text-gray-700" data-id="element-1077">
                    Recetas a base de <strong data-id="element-1078">pescado</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1079">
                  <Check className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1080" />
                  <span className="text-xs text-gray-700" data-id="element-1081">
                    Recetas <strong data-id="element-1082">mixtas</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1083">
                  <Check className="w-4 h-4 text-lime-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1084" />
                  <span className="text-xs text-gray-700" data-id="element-1085">
                    <strong data-id="element-1086">BONO #1 "Cómo Alargar la Vida de tu Perro"</strong>
                    <span className="ml-1 text-xs text-rose-700 font-bold" data-id="element-1087">
                      (GRATIS)
                    </span>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1088">
                  <Check className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1089" />
                  <span className="text-xs text-gray-700" data-id="element-1090">
                    <strong data-id="element-1091">
                      BONO #2 "Plan Express 7 Días: Transición Segura"
                    </strong>
                    <span className="ml-1 text-xs text-rose-700 font-bold" data-id="element-1092">
                      (GRATIS)
                    </span>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1093">
                  <Check className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1094" />
                  <span className="text-xs text-gray-700" data-id="element-1095">
                    <strong data-id="element-1096">
                      BONO #3 "Lista Negra + Lista Verde: Alimentos Seguros y
                      Prohibidos"
                    </strong>
                    <span className="ml-1 text-xs text-rose-700 font-bold" data-id="element-1097">
                      (GRATIS)
                    </span>
                  </span>
                </div>
              </div>

              {/* Bonos desplegable */}
              <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-1098">
                <button onClick={() => setShowBonos(!showBonos)} className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 transition-colors" data-id="element-1099">
                  {showBonos ? <>
                      <ChevronUp className="w-4 h-4" data-id="element-1100" />
                      Ocultar detalle de regalos
                    </> : <>
                      <ChevronDown className="w-4 h-4" data-id="element-1101" />
                      Ver qué incluyen los 3 regalos gratis
                    </>}
                </button>

                {showBonos && <div className="mt-3 space-y-3" data-id="element-1102">
                    <div className="flex items-start gap-3 bg-teal-50 p-3 rounded-xl border border-teal-100" data-id="element-1103">
                      <img src="https://cdn.magicpatterns.com/uploads/9pgdegNzbQ4opBiEgx4YKA/heylloyd-image-1769029546802.png" alt="Cómo Alargar la Vida de tu Perro" className="w-14 object-cover object-top rounded-lg border border-teal-200 flex-shrink-0" data-id="element-1104" />
                      <div data-id="element-1105">
                        <p className="text-sm font-bold text-gray-900" data-id="element-1106">
                          Cómo Alargar la Vida de tu Perro
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5" data-id="element-1107">
                          Guía de hábitos diarios: rutina simple, prevención y
                          listas de chequeo imprimibles.
                        </p>
                        <p className="text-xs text-teal-700 font-semibold mt-1" data-id="element-1108">
                          Valor: $49.999 COP — Hoy: GRATIS
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-xl border border-blue-100" data-id="element-1109">
                      <img src="https://cdn.magicpatterns.com/uploads/tmJCjjYa4iD56VcWj6sEtX/ChatGPT_Image_5_feb_2026,_12_53_16_p.m..png" alt="Plan Express 7 Días" className="w-14 object-cover object-top rounded-lg border border-blue-200 flex-shrink-0" data-id="element-1110" />
                      <div data-id="element-1111">
                        <p className="text-sm font-bold text-gray-900" data-id="element-1112">
                          Plan Express 7 Días: Transición Segura
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5" data-id="element-1113">
                          Plan paso a paso para cambiar a comida casera sin
                          diarrea ni malestar.
                        </p>
                        <p className="text-xs text-blue-700 font-semibold mt-1" data-id="element-1114">
                          Valor: $49.999 COP — Hoy: GRATIS
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-emerald-50 p-3 rounded-xl border border-emerald-100" data-id="element-1115">
                      <img src="https://cdn.magicpatterns.com/uploads/dhzCPguBEHAuB1vxaooPGU/ChatGPT_Image_5_feb_2026,_02_35_47_p.m..png" alt="Lista Negra + Lista Verde" className="w-14 object-cover object-top rounded-lg border border-emerald-200 flex-shrink-0" data-id="element-1116" />
                      <div data-id="element-1117">
                        <p className="text-sm font-bold text-gray-900" data-id="element-1118">
                          Lista Negra + Lista Verde
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5" data-id="element-1119">
                          Guía rápida de alimentos seguros, con cuidado y
                          prohibidos. Incluye hoja imprimible.
                        </p>
                        <p className="text-xs text-emerald-700 font-semibold mt-1" data-id="element-1120">
                          Valor: $49.999 COP — Hoy: GRATIS
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>

              {/* Giveaway */}
              <div className="mt-4 pt-4 border-t border-amber-200" data-id="element-1121">
                <div className="flex items-center gap-2 mb-2" data-id="element-1122">
                  <Gift className="w-4 h-4 text-rose-600" data-id="element-1123" />
                  <span className="text-xs font-bold text-rose-700 uppercase tracking-wide" data-id="element-1124">
                    Sorteo Especial
                  </span>
                </div>
                <p className="text-xs text-gray-700 mb-3" data-id="element-1125">
                  <strong data-id="element-1126">
                    Compra este e-book y participa por una cama y un comedero
                    para consentir a tu perro.
                  </strong>
                </p>
                <div className="flex items-center justify-center gap-3" data-id="element-1127">
                  <img src="https://cdn.magicpatterns.com/uploads/2vWbZS32sbqAgG7W3SwsJx/imagen_1x1.png" alt="Cama para perro" className="w-16 h-16 object-cover rounded-lg border-2 border-amber-200 shadow-sm" data-id="element-1128" />
                  <img src="https://cdn.magicpatterns.com/uploads/2Y1yUZV5roAZKNA5ktyjhw/imagen_plato_perro_1x1.png" alt="Comedero para perro" className="w-16 h-16 object-cover rounded-lg border-2 border-amber-200 shadow-sm" data-id="element-1129" />
                </div>
              </div>
            </div>

            {/* Audio Player */}
            <div className="mb-4 bg-amber-50 p-5 rounded-xl border-2 border-amber-200" data-id="element-1130">
              <div className="flex items-center gap-2 mb-3" data-id="element-1131">
                <Headphones className="w-5 h-5 text-amber-700" data-id="element-1132" />
                <span className="text-sm font-bold text-gray-800" data-id="element-1133">
                  Escucha un mensaje importante
                </span>
              </div>
              <audio controls preload="metadata" playsInline className="w-full" style={{
              minHeight: '40px'
            }} data-id="element-1134">
                <source src="https://ydgkmdmvhrniztrhtrxq.supabase.co/storage/v1/object/sign/audio%20receta/30%20recetas.mp3?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80NDNkYTY1ZS1iYWM1LTQyYWQtODMzNS1iNWE4N2M0NDYxNmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyByZWNldGEvMzAgcmVjZXRhcy5tcDMiLCJpYXQiOjE3NzEwMzYxODcsImV4cCI6MTgwMjU3MjE4N30.3HqKZiwbD6PGglTTdQ3GU_Eb0LcswvQcxF0yeKZmwBk" type="audio/mpeg" data-id="element-1135" />
                Tu navegador no soporta el elemento de audio.
              </audio>
              <p className="text-[10px] text-gray-400 mt-2" data-id="element-1136">
                Si no puedes reproducir el audio, intenta abrir la página en
                Chrome o Safari.
              </p>
            </div>

            {/* Pricing Card */}
            <div className="bg-gradient-to-br from-amber-50 to-stone-100 p-4 rounded-xl border border-amber-200 mb-4 relative overflow-hidden" data-id="element-1137">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-100 rounded-full blur-2xl opacity-50 -translate-y-1/2 translate-x-1/2" data-id="element-1138"></div>
              <div className="flex items-center justify-between mb-3 relative z-10" data-id="element-1139">
                <div data-id="element-1140">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1141">
                    <span className="text-xs text-gray-500" data-id="element-1142">Valor real:</span>
                    <span className="text-sm text-gray-400 line-through" data-id="element-1143">
                      $79.000 COP
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1144">
                    <ArrowRight className="w-3 h-3 text-gray-600" data-id="element-1145" />
                    <span className="text-xs text-gray-600 font-medium" data-id="element-1146">
                      HOY SOLO:
                    </span>
                    <span className="text-3xl font-bold text-amber-700" data-id="element-1147">
                      ${orderTotal.toLocaleString('es-CO')} COP
                    </span>
                  </div>
                </div>
                <div className="bg-rose-700 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide animate-pulse h-fit" data-id="element-1148">
                  Oferta
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs text-gray-600 relative z-10 font-medium" data-id="element-1149">
                <span className="bg-white px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1150">
                  <Check className="w-3 h-3 text-green-500" data-id="element-1151" /> Pago único
                </span>
                <span className="bg-white px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1152">
                  <Check className="w-3 h-3 text-green-500" data-id="element-1153" /> Acceso inmediato
                </span>
                <span className="bg-white px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1154">
                  <Check className="w-3 h-3 text-green-500" data-id="element-1155" /> Acceso de por
                  vida
                </span>
              </div>
            </div>

            {/* CTA Button */}
            <a href={activeCheckoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full" onClick={handleCheckoutClick} data-id="element-1156">
              <button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-5 px-8 rounded-xl shadow-lg shadow-green-200 transition-all duration-200 hover:-translate-y-1 flex flex-col items-center justify-center gap-1 text-center leading-tight" data-id="element-1157">
                <span className="text-lg" data-id="element-1158">COMPRAR AHORA</span>
                <span className="text-sm opacity-90" data-id="element-1159">
                  por ${orderTotal.toLocaleString('es-CO')} COP
                </span>
              </button>
            </a>
            <p className="text-center text-xs font-semibold mt-3 animate-pulse" style={{
            color: '#d97706',
            textShadow: '0 0 8px rgba(217, 119, 6, 0.5), 0 0 16px rgba(217, 119, 6, 0.3)'
          }} data-id="element-1160">
              ❤️ Si consideras a tu perro familia y lo amas, no lo pienses más
            </p>

            {/* Order Bumps */}
            <div className="mt-6 space-y-4" data-id="element-1161">
              <div className="text-center" data-id="element-1162">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 uppercase tracking-wider mb-1.5" data-id="element-1163">
                  <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" data-id="element-1164" />
                  Altamente recomendado
                  <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" data-id="element-1165" />
                </span>
              </div>

              {/* Order Bump 1 */}
              <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${bump1Selected ? 'border-amber-500 shadow-lg shadow-amber-100' : 'border-amber-200 hover:border-amber-300'}`} onClick={() => setBump1Selected(!bump1Selected)} data-id="element-1166">
                <div className="order-bump-flash bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 flex items-center justify-between" data-id="element-1167">
                  <div className="flex items-center gap-3" data-id="element-1168">
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${bump1Selected ? 'bg-white border-white shadow-md' : 'border-white bg-white/20'}`} data-id="element-1169">
                      {bump1Selected && <Check className="w-4 h-4 text-amber-600" strokeWidth={3} data-id="element-1170" />}
                    </div>
                    <span className="text-white text-xs font-bold uppercase tracking-wide" data-id="element-1171">
                      ¡Sí, lo quiero! — Agregar a mi pedido
                    </span>
                  </div>
                </div>
                <div className="w-full" data-id="element-1172">
                  <img src="https://cdn.magicpatterns.com/uploads/fsD5rTTzoVEYKeEw4P6S3y/order_1.png" alt="Cocina 1 vez por semana" className="w-full h-auto object-cover" data-id="element-1173" />
                </div>
                <div className="bg-amber-50 p-4" data-id="element-1174">
                  <h5 className="text-base md:text-lg font-extrabold text-gray-900 leading-snug" data-id="element-1175">
                    Cocina 1 vez por semana (sin improvisar)
                  </h5>
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-1176">
                    Convierte las 30 recetas en un sistema semanal: eliges,
                    cocinas por lotes y porcionas.
                  </p>
                  <div className="mt-3 space-y-1.5" data-id="element-1177">
                    <div className="flex items-start gap-2" data-id="element-1178">
                      <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1179" />
                      <span className="text-xs text-gray-700" data-id="element-1180">
                        Menos tiempo diario
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1181">
                      <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1182" />
                      <span className="text-xs text-gray-700" data-id="element-1183">
                        Rotación sin caos digestivo
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1184">
                      <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1185" />
                      <span className="text-xs text-gray-700" data-id="element-1186">
                        Audioguía manos libres incluida
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-2 bg-white border-2 border-amber-300 rounded-lg px-4 py-2.5" data-id="element-1187">
                    <Tag className="w-4 h-4 text-amber-600" data-id="element-1188" />
                    <span className="text-lg font-extrabold text-amber-700" data-id="element-1189">
                      + $19.000 COP
                    </span>
                  </div>
                  <div className="mt-2 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-1190">
                    <Check className="w-4 h-4 text-amber-700 flex-shrink-0" strokeWidth={3} data-id="element-1191" />
                    <span className="text-sm font-bold text-amber-800" data-id="element-1192">
                      PDF + audios MP3 — descarga inmediata
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-center" data-id="element-1193">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-600 uppercase tracking-wider mb-1.5" data-id="element-1194">
                  <Star className="w-3.5 h-3.5 fill-teal-400 text-teal-400" data-id="element-1195" />
                  Altamente recomendado
                  <Star className="w-3.5 h-3.5 fill-teal-400 text-teal-400" data-id="element-1196" />
                </span>
              </div>

              {/* Order Bump 2 */}
              <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${bump2Selected ? 'border-teal-500 shadow-lg shadow-teal-100' : 'border-teal-200 hover:border-teal-300'}`} onClick={() => setBump2Selected(!bump2Selected)} data-id="element-1197">
                <div className="order-bump-flash bg-gradient-to-r from-teal-500 to-emerald-500 px-4 py-2.5 flex items-center justify-between" data-id="element-1198">
                  <div className="flex items-center gap-3" data-id="element-1199">
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${bump2Selected ? 'bg-white border-white shadow-md' : 'border-white bg-white/20'}`} data-id="element-1200">
                      {bump2Selected && <Check className="w-4 h-4 text-teal-600" strokeWidth={3} data-id="element-1201" />}
                    </div>
                    <span className="text-white text-xs font-bold uppercase tracking-wide" data-id="element-1202">
                      ¡Sí, lo quiero! — Agregar a mi pedido
                    </span>
                  </div>
                </div>
                <div className="w-full" data-id="element-1203">
                  <img src="https://cdn.magicpatterns.com/uploads/xgRuFDo3r8Pj8fZw2d5EnX/order_2.png" alt="Compra bien sin gastar de más" className="w-full h-auto object-cover" data-id="element-1204" />
                </div>
                <div className="bg-teal-50 p-4" data-id="element-1205">
                  <h5 className="text-base md:text-lg font-extrabold text-gray-900 leading-snug" data-id="element-1206">
                    Compra bien (sin gastar de más)
                  </h5>
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-1207">
                    Ingredientes económicos + sustituciones rápidas + reglas
                    simples.
                  </p>
                  <div className="mt-3 space-y-1.5" data-id="element-1208">
                    <div className="flex items-start gap-2" data-id="element-1209">
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1210" />
                      <span className="text-xs text-gray-700" data-id="element-1211">
                        Lista de swaps por proteína
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1212">
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1213" />
                      <span className="text-xs text-gray-700" data-id="element-1214">
                        Recetas "baratas" favoritas
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1215">
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-1216" />
                      <span className="text-xs text-gray-700" data-id="element-1217">
                        Reglas para no disparar el gasto
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-2 bg-white border-2 border-teal-300 rounded-lg px-4 py-2.5" data-id="element-1218">
                    <Tag className="w-4 h-4 text-teal-600" data-id="element-1219" />
                    <span className="text-lg font-extrabold text-teal-700" data-id="element-1220">
                      + $17.000 COP
                    </span>
                  </div>
                  <div className="mt-2 bg-teal-100 border border-teal-300 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-1221">
                    <Check className="w-4 h-4 text-teal-700 flex-shrink-0" strokeWidth={3} data-id="element-1222" />
                    <span className="text-sm font-bold text-teal-800" data-id="element-1223">
                      PDF + audios MP3 — precio especial hoy en tu compra
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="mt-5 bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1224">
              <div className="flex items-center gap-2 mb-3" data-id="element-1225">
                <ShoppingCart className="w-4 h-4 text-gray-700" data-id="element-1226" />
                <span className="text-sm font-bold text-gray-800" data-id="element-1227">
                  Tu pedido
                </span>
              </div>
              <div className="space-y-2" data-id="element-1228">
                <div className="flex items-center justify-between" data-id="element-1229">
                  <div className="flex items-center gap-2" data-id="element-1230">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1231" />
                    <span className="text-xs text-gray-700" data-id="element-1232">
                      30 Recetas + 3 Regalos Gratis
                    </span>
                  </div>
                  <span className="text-xs font-bold text-gray-800" data-id="element-1233">
                    $19.999
                  </span>
                </div>
                {bump1Selected && <div className="flex items-center justify-between" data-id="element-1234">
                    <div className="flex items-center gap-2" data-id="element-1235">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-1236" />
                      <span className="text-xs text-gray-700" data-id="element-1237">
                        Cocina 1 vez por semana
                      </span>
                    </div>
                    <span className="text-xs font-bold text-gray-800" data-id="element-1238">
                      $19.000
                    </span>
                  </div>}
                {bump2Selected && <div className="flex items-center justify-between" data-id="element-1239">
                    <div className="flex items-center gap-2" data-id="element-1240">
                      <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" data-id="element-1241" />
                      <span className="text-xs text-gray-700" data-id="element-1242">
                        Compra bien (sin gastar de más)
                      </span>
                    </div>
                    <span className="text-xs font-bold text-gray-800" data-id="element-1243">
                      $17.000
                    </span>
                  </div>}
              </div>
              <div className="mt-3 pt-3 border-t-2 border-dashed border-gray-200 flex items-center justify-between" data-id="element-1244">
                <span className="text-sm font-extrabold text-gray-900" data-id="element-1245">
                  TOTAL A PAGAR:
                </span>
                <span className="text-xl font-extrabold text-green-600" data-id="element-1246">
                  ${orderTotal.toLocaleString('es-CO')} COP
                </span>
              </div>
              <a href={activeCheckoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full mt-4" onClick={handleCheckoutClick} data-id="element-1247">
                <button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-green-200 transition-all duration-200 hover:-translate-y-1 flex flex-col items-center justify-center gap-1" data-id="element-1248">
                  <span className="text-base md:text-lg leading-tight" data-id="element-1249">
                    COMPRAR AHORA
                  </span>
                  <span className="text-sm opacity-90" data-id="element-1250">
                    por ${orderTotal.toLocaleString('es-CO')} COP
                  </span>
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}